import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as THREE from 'three';
import { FaCalendarAlt, FaClock, FaWhatsapp, FaArrowLeft, FaFilter, FaSearch, FaFilePdf, FaBell, FaUser, FaBook } from 'react-icons/fa';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { Helmet } from 'react-helmet';
import { usePDF } from 'react-to-pdf';
import { fetchDayLessons, setSelectedDate } from '../../redux/slices/dayLessonsSlice';
import styles from './ScheduleTable.module.css';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const ScheduleTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDarkMode } = useDarkMode();
  const { currentUser } = useSelector((state) => state.auth);
  const { lessons, loading, error } = useSelector((state) => state.dayLessons);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const scheduleRef = useRef(null);
  const pdfRef = useRef(null);
  const [selectedDate, setSelectedDateState] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showReminders, setShowReminders] = useState(false);
  const [reminders, setReminders] = useState([]);
  
  // Check authentication and role
  const { isAuthenticated } = useSelector((state) => state.auth);
  const userRole = currentUser?.role || localStorage.getItem("userRole");
  const isStudent = userRole === "student";
  const token = localStorage.getItem('token3almny');
  
  // Redirect logic: غير مسجل → login, مسجل لكن ليس طالب → الرئيسية
  useEffect(() => {
    // إذا لم يكن مسجل دخول، اذهب لصفحة تسجيل الدخول
    if (!token && !isAuthenticated) {
      navigate("/login");
      return;
    }
    
    // إذا كان مسجل دخول لكن ليس طالب، اذهب للرئيسية
    if ((token || isAuthenticated) && userRole && !isStudent) {
      navigate("/");
      return;
    }
  }, [token, isAuthenticated, userRole, isStudent, navigate]);
  
  // PDF hook
  const { toPDF, targetRef: pdfTargetRef } = usePDF({ 
    filename: `schedule_${selectedDate.getDate()}_${selectedMonth + 1}_${selectedYear}.pdf`,
    page: { 
      margin: 20,
      format: 'a4'
    },
    canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: true
    }
  });

  // Load reminders from localStorage on component mount
  useEffect(() => {
    const savedReminders = localStorage.getItem('scheduleReminders');
    if (savedReminders) {
      try {
        const parsedReminders = JSON.parse(savedReminders);
        // Convert date strings back to Date objects
        const remindersWithDates = parsedReminders.map(reminder => ({
          ...reminder,
          date: new Date(reminder.date),
          createdAt: new Date(reminder.createdAt)
        }));
        setReminders(remindersWithDates);
      } catch (error) {
        setReminders([]);
      }
    }
  }, []);

  // Fetch lessons when date changes
  useEffect(() => {
    if (isStudent && selectedDate) {
      const formattedDate = selectedDate.toISOString().split('T')[0]; // Format: "2025-08-14"
      dispatch(fetchDayLessons(formattedDate));
    }
  }, [selectedDate, dispatch, isStudent]);

  // Update filtered classes when lessons change
  useEffect(() => {
    if (lessons && Array.isArray(lessons)) {
      let filtered = lessons;
      
      if (searchTerm.trim()) {
        filtered = lessons.filter(lesson => 
          lesson.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lesson.teacher?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setFilteredClasses(filtered);
    } else {
      setFilteredClasses([]);
    }
  }, [lessons, searchTerm]);

  // Handle API errors silently
  useEffect(() => {
    if (error) {
      console.error('Day lessons error:', error);
    }
  }, [error]);

  // Note: Reminders are saved manually in addReminder and removeReminder functions

  // Helper function to format time from backend data
  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    // Assuming time comes as "HH:MM:SS" or "HH:MM"
    return timeString.split(':').slice(0, 2).join(':');
  };

  // Helper function to format duration
  const formatDuration = (duration) => {
    if (!duration) return '-- دقيقة';
    // Assuming duration comes in minutes or seconds
    if (typeof duration === 'number') {
      return duration >= 60 ? `${Math.floor(duration / 60)} دقيقة` : `${duration} دقيقة`;
    }
    return duration;
  };

  const daysOfWeek = [
    { name: 'السبت', key: 'saturday', arabic: 'السبت' },
    { name: 'الأحد', key: 'sunday', arabic: 'الأحد' },
    { name: 'الاثنين', key: 'monday', arabic: 'الاثنين' },
    { name: 'الثلاثاء', key: 'tuesday', arabic: 'الثلاثاء' },
    { name: 'الأربعاء', key: 'wednesday', arabic: 'الأربعاء' },
    { name: 'الخميس', key: 'thursday', arabic: 'الخميس' },
    { name: 'الجمعة', key: 'friday', arabic: 'الجمعة' }
  ];

  const months = [
    'يناير', 'فبراير', 'مارس', 'إبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  // Helper function to get Arabic day of week
  const getArabicDayOfWeek = (date) => {
    const day = date.getDay();
    // Convert: 0=Sunday, 1=Monday, ..., 6=Saturday
    // To: 0=Saturday, 1=Sunday, ..., 6=Friday
    const arabicDayIndex = day === 0 ? 6 : day - 1;
    return daysOfWeek[arabicDayIndex];
  };



  // Three.js background animation
  useEffect(() => {
    if (!canvasRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ 
      canvas: canvasRef.current, 
      alpha: true,
      antialias: true 
    });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);

    // Create floating particles
    const particles = [];
    const particleCount = 50;
    const geometry = new THREE.SphereGeometry(0.1, 8, 8);
    const material = new THREE.MeshBasicMaterial({ 
      color: isDarkMode ? 0x4a90e2 : 0x017FD2,
      transparent: true,
      opacity: 0.6
    });

    for (let i = 0; i < particleCount; i++) {
      const particle = new THREE.Mesh(geometry, material);
      particle.position.set(
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20
      );
      particle.velocity = {
        x: (Math.random() - 0.5) * 0.02,
        y: (Math.random() - 0.5) * 0.02,
        z: (Math.random() - 0.5) * 0.02
      };
      particles.push(particle);
      scene.add(particle);
    }

    camera.position.z = 5;

    const animate = () => {
      requestAnimationFrame(animate);

      particles.forEach(particle => {
        particle.position.x += particle.velocity.x;
        particle.position.y += particle.velocity.y;
        particle.position.z += particle.velocity.z;

        // Bounce off boundaries
        if (Math.abs(particle.position.x) > 10) particle.velocity.x *= -1;
        if (Math.abs(particle.position.y) > 10) particle.velocity.y *= -1;
        if (Math.abs(particle.position.z) > 10) particle.velocity.z *= -1;

        particle.rotation.x += 0.01;
        particle.rotation.y += 0.01;
      });

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
    };
  }, [isDarkMode]);

  // Update particles color when dark mode changes
  useEffect(() => {
    if (canvasRef.current) {
      const material = canvasRef.current.material;
      if (material) {
        material.color.setHex(isDarkMode ? 0x4a90e2 : 0x017FD2);
      }
    }
  }, [isDarkMode]);

  // GSAP animations
  useEffect(() => {
    if (!containerRef.current) return;

    const tl = gsap.timeline();

    // Use refs instead of CSS selectors
    const headerElement = containerRef.current.querySelector('[data-section="header"]');
    const searchElement = containerRef.current.querySelector('[data-section="search"]');
    const calendarElement = containerRef.current.querySelector('[data-section="calendar"]');
    const tableElement = containerRef.current.querySelector('[data-section="table"]');

    if (headerElement) {
      tl.fromTo(headerElement, 
        { y: -50, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power2.out' }
      );
    }

    if (searchElement) {
      tl.fromTo(searchElement, 
        { x: -50, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
        '-=0.4'
      );
    }

    if (calendarElement) {
      tl.fromTo(calendarElement, 
        { scale: 0.9, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.8, ease: 'back.out(1.7)' },
        '-=0.3'
      );
    }

    if (tableElement) {
      tl.fromTo(tableElement, 
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power2.out' },
        '-=0.4'
      );
    }

    // Animate table rows on scroll
    const classItems = containerRef.current.querySelectorAll('[data-section="class-item"]');
    classItems.forEach((row, index) => {
      gsap.fromTo(row,
        { x: -100, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.6,
          delay: index * 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: row,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [filteredClasses]);

  // Filter classes based on selected date and search term - removed room from search
  useEffect(() => {
    if (lessons && Array.isArray(lessons)) {
      let filtered = lessons;
      
      if (searchTerm.trim()) {
        filtered = lessons.filter(lesson => 
          lesson.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lesson.teacher?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        // Scroll to schedule table when searching
        setTimeout(() => {
          const tableElement = scheduleRef.current;
          if (tableElement) {
            const headerHeight = 120; // Approximate header height
            const elementTop = tableElement.offsetTop - headerHeight;
            window.scrollTo({
              top: elementTop,
              behavior: 'smooth'
            });
          }
        }, 300);
      }
      
      setFilteredClasses(filtered);
    } else {
      setFilteredClasses([]);
    }
  }, [lessons, searchTerm]);

  const handleDateChange = (day) => {
    const newDate = new Date(selectedYear, selectedMonth, day);
    setSelectedDateState(newDate);
    
    // Scroll to schedule table
    setTimeout(() => {
      const tableElement = scheduleRef.current;
      if (tableElement) {
        tableElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
    
    // Animate the change
    const classItems = containerRef.current?.querySelectorAll('[data-section="class-item"]');
    if (classItems) {
      gsap.fromTo(classItems,
        { x: -20, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.4, ease: 'power2.out', stagger: 0.1 }
      );
    }
  };

  const handleMonthChange = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;

    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);

    // Animate month change
    const monthDisplay = containerRef.current?.querySelector('[data-section="month-display"]');
    if (monthDisplay) {
      gsap.fromTo(monthDisplay,
        { scale: 0.8, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.7)' }
      );
    }
  };

  const handleWhatsAppShare = async () => {
    setIsLoading(true);
    
    try {
      // Simulate screenshot process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const message = `جدول الحصص ليوم ${getArabicDayOfWeek(selectedDate).arabic} ${selectedDate.getDate()} ${months[selectedMonth]} ${selectedYear}\n\n` +
        filteredClasses.map(cls => 
          `${formatTime(cls.time || cls.start_time)} - ${cls.subject || cls.subject_name || 'مادة غير محددة'} - ${cls.teacher || cls.teacher_name || 'مدرس غير محدد'}`
        ).join('\n');
      
      const whatsappUrl = `https://wa.me/201025376604?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      // Success animation
      const whatsappBtn = containerRef.current?.querySelector('[data-section="whatsapp-btn"]');
      if (whatsappBtn) {
        gsap.to(whatsappBtn, {
          scale: 1.1,
          duration: 0.2,
          yoyo: true,
          repeat: 1
        });
      }
      
    } catch (error) {
      // Error sharing to WhatsApp
    } finally {
      setIsLoading(false);
    }
  };

  const handlePDFExport = async () => {
    setIsLoading(true);
    
    try {
      // Generate PDF using the hook
      if (pdfTargetRef.current) {
        await toPDF();
        showNotification('تم إنشاء ملف PDF بنجاح', 'success');
        
        // Success animation
        const pdfBtn = containerRef.current?.querySelector('[data-section="pdf-btn"]');
        if (pdfBtn) {
          gsap.to(pdfBtn, {
            scale: 1.1,
            duration: 0.2,
            yoyo: true,
            repeat: 1
          });
        }
      } else {
        throw new Error('PDF content not found');
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      showNotification('حدث خطأ أثناء إنشاء ملف PDF', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReminderToggle = () => {
    setShowReminders(!showReminders);
  };

  const addReminder = (classItem) => {
    const reminder = {
      id: Date.now(),
      class: classItem,
      date: new Date(selectedDate), // Ensure it's a proper Date object
      time: classItem.time,
      subject: classItem.subject,
      teacher: classItem.teacher,
      createdAt: new Date()
    };
    
    // Check if reminder already exists for this class and date
    const existingReminder = reminders.find(r => {
      const reminderDate = new Date(r.date);
      return r.subject === classItem.subject && 
             r.time === classItem.time && 
             reminderDate.getDate() === selectedDate.getDate() &&
             reminderDate.getMonth() === selectedDate.getMonth() &&
             reminderDate.getFullYear() === selectedDate.getFullYear();
    });
    
    if (existingReminder) {
      showNotification('هذا التذكير موجود بالفعل!', 'warning');
      return;
    }
    
    const newReminders = [...reminders, reminder];
    setReminders(newReminders);
    
    // Save to localStorage immediately
    localStorage.setItem('scheduleReminders', JSON.stringify(newReminders));
    
    // Success animation
    const reminderBtn = containerRef.current?.querySelector('[data-section="reminder-btn"]');
    if (reminderBtn) {
      gsap.to(reminderBtn, {
        scale: 1.2,
        duration: 0.2,
        yoyo: true,
        repeat: 1
      });
    }
    
    // Show success message
    const successMessage = `تم إضافة تذكير لـ ${classItem.subject} في ${classItem.time}`;
    showNotification(successMessage, 'success');
  };

  const removeReminder = (reminderId) => {
    const newReminders = reminders.filter(r => r.id !== reminderId);
    setReminders(newReminders);
    
    // Save to localStorage immediately
    localStorage.setItem('scheduleReminders', JSON.stringify(newReminders));
    
    showNotification('تم حذف التذكير', 'info');
  };

  const showNotification = (message, type = 'info') => {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `${styles['jadwal-notification']} ${styles[`jadwal-notification-${type}`]}`;
    notification.textContent = message;
    
    // Add to page
    document.body.appendChild(notification);
    
    // Animate in
    gsap.fromTo(notification, 
      { opacity: 0, y: -50 },
      { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
    );
    
    // Remove after 3 seconds
    setTimeout(() => {
      gsap.to(notification, {
        opacity: 0,
        y: -50,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: () => {
          document.body.removeChild(notification);
        }
      });
    }, 3000);
  };

  const clearAllReminders = () => {
    const todayReminders = getRemindersForToday();
    if (todayReminders.length === 0) {
      showNotification('لا توجد تذكيرات لحذفها', 'warning');
      return;
    }
    
    if (window.confirm('هل أنت متأكد من حذف جميع تذكيرات اليوم؟')) {
      const today = new Date();
      const newReminders = reminders.filter(reminder => {
        const reminderDate = new Date(reminder.date);
        return !(reminderDate.getDate() === today.getDate() &&
                reminderDate.getMonth() === today.getMonth() &&
                reminderDate.getFullYear() === today.getFullYear());
      });
      
      setReminders(newReminders);
      
      // Save to localStorage immediately
      localStorage.setItem('scheduleReminders', JSON.stringify(newReminders));
      
      showNotification('تم حذف جميع تذكيرات اليوم', 'success');
    }
  };

  const getRemindersForToday = () => {
    const today = new Date();
    return reminders.filter(reminder => {
      const reminderDate = new Date(reminder.date);
      return reminderDate.getDate() === today.getDate() &&
             reminderDate.getMonth() === today.getMonth() &&
             reminderDate.getFullYear() === today.getFullYear();
    });
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // If there's a search term, scroll to table immediately
    if (value.trim()) {
      setTimeout(() => {
        const tableElement = scheduleRef.current;
        if (tableElement) {
          const headerHeight = 120; // Approximate header height
          const elementTop = tableElement.offsetTop - headerHeight;
          window.scrollTo({
            top: elementTop,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    // Get the day of week for the first day of the month
    const day = new Date(year, month, 1).getDay();
    // In JavaScript: 0=Sunday, 1=Monday, ..., 6=Saturday
    // In Arabic calendar: 0=Saturday, 1=Sunday, ..., 6=Friday
    // So we need to convert: Sunday(0) -> 1, Monday(1) -> 2, ..., Saturday(6) -> 0
    // But since our calendar starts with Saturday, we need to adjust
    return (day + 1) % 7;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles['jadwal-calendar-day-empty']}></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = day === selectedDate.getDate() && 
                        selectedMonth === selectedDate.getMonth() && 
                        selectedYear === selectedDate.getFullYear();
      
      days.push(
        <div
          key={day}
          className={`${styles['jadwal-calendar-day']} ${isSelected ? styles['jadwal-calendar-day-selected'] : ''}`}
          onClick={() => handleDateChange(day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const highlightSearchTerm = (text) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className={styles['jadwal-highlight']}>
          {part}
        </span>
      ) : part
    );
  };

  return (
    <div className={`${styles['jadwal-page']} ${isDarkMode ? styles['dark-mode'] : ''}`} ref={containerRef}>
      <Helmet>
        <title>جدول المواعيد - علمني</title>
        <meta name="description" content="جدول الحصص الأسبوعي - عرض مواعيد الحصص والمدرسين" />
        <meta name="keywords" content="جدول مواعيد, حصص, مدرسين, علمني, تعليم" />
      </Helmet>

      {/* Three.js Background */}
      <canvas ref={canvasRef} className={styles['jadwal-background-canvas']} />

      {/* Header */}
      <div className={styles['jadwal-header']} data-section="header">
        <button 
          className={styles['jadwal-back-btn']}
          onClick={() => navigate('/')}
        >
          <FaArrowLeft />
          الرئيسية
        </button>
        <h1 className={styles['jadwal-title']}>جدول المواعيد</h1>
        <div className={styles['jadwal-subtitle']}>نظام إدارة الحصص الدراسية</div>
      </div>

      {/* Search Bar */}
      <div className={styles['jadwal-search']} data-section="search">
        <div className={styles['jadwal-search-container']}>
          <FaSearch className={styles['jadwal-search-icon']} />
          <input
            type="text"
            placeholder="ابحث عن مادة أو مدرس..."
            value={searchTerm}
            onChange={handleSearch}
            className={styles['jadwal-search-input']}
          />
        </div>
      </div>

      {/* Calendar */}
      <div className={styles['jadwal-calendar']} data-section="calendar">
        <div className={styles['jadwal-month-navigation']}>
          <button 
            className={styles['jadwal-month-btn']}
            onClick={() => handleMonthChange(-1)}
          >
            ‹
          </button>
          <div className={styles['jadwal-month-display']} data-section="month-display">
            {months[selectedMonth]} {selectedYear}
          </div>
          <button 
            className={styles['jadwal-month-btn']}
            onClick={() => handleMonthChange(1)}
          >
            ›
          </button>
        </div>
        
        <div className={styles['jadwal-calendar-grid']}>
          <div className={styles['jadwal-calendar-header']}>
            {daysOfWeek.map(day => (
              <div key={day.key} className={styles['jadwal-calendar-day-header']}>
                {day.arabic}
              </div>
            ))}
          </div>
          <div className={styles['jadwal-calendar-days']}>
            {renderCalendar()}
          </div>
        </div>
      </div>

      {/* Schedule Table */}
      <div className={`${styles['jadwal-table']} ${loading ? styles.loading : ''}`} ref={scheduleRef} data-section="table">
        <div className={styles['jadwal-table-header']}>
          <h2 className={styles['jadwal-table-title']}>
            جدول الحصص - {getArabicDayOfWeek(selectedDate).arabic} {selectedDate.getDate()} {months[selectedMonth]}
          </h2>
          <div className={styles['jadwal-actions']}>
            <button 
              className={styles['jadwal-reminder-btn']}
              onClick={handleReminderToggle}
              title="التذكيرات"
              data-section="reminder-btn"
            >
              <FaBell />
              <span className={styles['jadwal-reminder-count']}>
                {getRemindersForToday().length}
              </span>
            </button>
            <button 
              className={styles['jadwal-pdf-btn']}
              onClick={handlePDFExport}
              disabled={isLoading}
              title="تصدير PDF"
              data-section="pdf-btn"
            >
              <FaFilePdf />
              PDF
            </button>
            <button 
              className={styles['jadwal-whatsapp-btn']}
              onClick={handleWhatsAppShare}
              disabled={isLoading}
              title="إرسال للواتساب"
              data-section="whatsapp-btn"
            >
              {isLoading ? (
                <div className={styles['jadwal-loading-spinner']} />
              ) : (
                <>
                  <FaWhatsapp />
                  واتساب
                </>
              )}
            </button>
          </div>
        </div>

        <div className={styles['jadwal-classes']}>
          {filteredClasses.length > 0 ? (
            filteredClasses.map((cls, index) => (
              <div key={index} className={styles['jadwal-class-item']} data-section="class-item">
                <div className={styles['jadwal-class-time']}>
                  <FaClock />
                  <span>{formatTime(cls.time || cls.start_time)}</span>
                  <small>{formatDuration(cls.duration || cls.lesson_duration)}</small>
                </div>
                <div className={styles['jadwal-class-info']}>
                  <div className={styles['jadwal-class-subject-container']}>
                    <FaBook className={styles['jadwal-class-icon']} />
                    <h3 className={styles['jadwal-class-subject']}>
                      {highlightSearchTerm(cls.subject || cls.subject_name || 'مادة غير محددة')}
                    </h3>
                  </div>
                  <div className={styles['jadwal-class-teacher-container']}>
                    <FaUser className={styles['jadwal-class-icon']} />
                    <p className={styles['jadwal-class-teacher']}>
                      {highlightSearchTerm(cls.teacher || cls.teacher_name || 'مدرس غير محدد')}
                    </p>
                  </div>
                </div>
                <div className={styles['jadwal-class-actions']}>
                  <button
                    className={styles['jadwal-add-reminder-btn']}
                    onClick={() => addReminder(cls)}
                    title="إضافة تذكير"
                  >
                    <FaBell />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className={styles['jadwal-no-classes']}>
              <FaCalendarAlt />
              <p>
                {loading 
                  ? 'جاري تحميل الحصص...'
                  : searchTerm.trim() 
                    ? 'لا توجد نتائج للبحث المحدد' 
                    : 'لا توجد حصص في هذا اليوم'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* PDF Content (Hidden) */}
      <div ref={pdfTargetRef} className={styles['jadwal-pdf-content']} style={{ 
        position: 'absolute',
        top: '-10000px',
        left: '-10000px',
        width: '800px',
        zIndex: '-1',
        backgroundColor: 'white',
        fontFamily: 'Arial, sans-serif',
        direction: 'rtl',
        textAlign: 'right'
      }}>
        {/* Header with Logo */}
        <div style={{
          background: 'linear-gradient(135deg, #017FD2 0%, #0056b3 100%)',
          padding: '30px 40px',
          color: 'white',
          borderRadius: '0 0 20px 20px',
          marginBottom: '30px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                backgroundColor: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#017FD2'
              }}>
                3A
              </div>
              <div>
                <h1 style={{
                  margin: '0',
                  fontSize: '32px',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  علمني
                </h1>
                <p style={{
                  margin: '5px 0 0 0',
                  fontSize: '14px',
                  opacity: '0.9'
                }}>
                  نظام إدارة الحصص الدراسية
                </p>
              </div>
            </div>
            <div style={{
              textAlign: 'left',
              fontSize: '12px',
              opacity: '0.8'
            }}>
              <p style={{ margin: '2px 0' }}>تاريخ الإنشاء</p>
              <p style={{ margin: '2px 0', fontWeight: 'bold' }}>
                {new Date().toLocaleDateString('ar-EG')}
              </p>
            </div>
          </div>
          
          <div style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '15px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <h2 style={{
              margin: '0 0 10px 0',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
              جدول الحصص الأسبوعي
            </h2>
            <p style={{
              margin: '0',
              fontSize: '18px',
              opacity: '0.9'
            }}>
              {getArabicDayOfWeek(selectedDate).arabic} {selectedDate.getDate()} {months[selectedMonth]} {selectedYear}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ padding: '0 40px 40px 40px' }}>
          {/* Info Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '20px',
              borderRadius: '15px',
              border: '2px solid #e9ecef',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                color: '#017FD2',
                marginBottom: '10px'
              }}>
                📅
              </div>
              <h4 style={{
                margin: '0 0 5px 0',
                color: '#333',
                fontSize: '14px'
              }}>
                التاريخ
              </h4>
              <p style={{
                margin: '0',
                color: '#017FD2',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                {selectedDate.getDate()} {months[selectedMonth]} {selectedYear}
              </p>
            </div>
            
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '20px',
              borderRadius: '15px',
              border: '2px solid #e9ecef',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                color: '#017FD2',
                marginBottom: '10px'
              }}>
                🌟
              </div>
              <h4 style={{
                margin: '0 0 5px 0',
                color: '#333',
                fontSize: '14px'
              }}>
                اليوم
              </h4>
              <p style={{
                margin: '0',
                color: '#017FD2',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                {getArabicDayOfWeek(selectedDate).arabic}
              </p>
            </div>
            
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '20px',
              borderRadius: '15px',
              border: '2px solid #e9ecef',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                color: '#017FD2',
                marginBottom: '10px'
              }}>
                📚
              </div>
              <h4 style={{
                margin: '0 0 5px 0',
                color: '#333',
                fontSize: '14px'
              }}>
                عدد الحصص
              </h4>
              <p style={{
                margin: '0',
                color: '#017FD2',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                {filteredClasses.length}
              </p>
            </div>
          </div>

          {/* Classes Section */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              marginBottom: '25px',
              paddingBottom: '15px',
              borderBottom: '3px solid #017FD2'
            }}>
              <div style={{
                fontSize: '28px',
                color: '#017FD2'
              }}>
                🎓
              </div>
              <h3 style={{
                margin: '0',
                color: '#017FD2',
                fontSize: '22px',
                fontWeight: 'bold'
              }}>
                الحصص المقررة
              </h3>
            </div>
            
            {filteredClasses.length > 0 ? (
              <div style={{
                display: 'grid',
                gap: '20px'
              }}>
                {filteredClasses.map((cls, index) => (
                  <div key={index} style={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '15px',
                    padding: '25px',
                    border: '2px solid #e9ecef',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      width: '100px',
                      height: '100px',
                      background: 'linear-gradient(135deg, rgba(1,127,210,0.1) 0%, rgba(1,127,210,0.05) 100%)',
                      borderRadius: '0 15px 0 100px'
                    }}></div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      position: 'relative',
                      zIndex: '1'
                    }}>
                      <div style={{
                        backgroundColor: '#017FD2',
                        color: 'white',
                        padding: '12px 18px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        minWidth: '90px',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(1,127,210,0.3)'
                      }}>
                        {cls.time}
                      </div>
                      
                      <div style={{ flex: '1' }}>
                        <h4 style={{
                          margin: '0 0 8px 0',
                          color: '#333',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}>
                          {cls.subject || cls.subject_name || 'مادة غير محددة'}
                        </h4>
                        <p style={{
                          margin: '0 0 5px 0',
                          color: '#666',
                          fontSize: '15px'
                        }}>
                          👨‍🏫 {cls.teacher || cls.teacher_name || 'مدرس غير محدد'}
                        </p>
                        <div style={{
                          display: 'inline-block',
                          backgroundColor: '#e9ecef',
                          color: '#666',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ⏱️ {formatDuration(cls.duration || cls.lesson_duration)}
                        </div>
                      </div>
                      
                      <div style={{
                        fontSize: '24px',
                        color: '#017FD2',
                        opacity: '0.7'
                      }}>
                        📖
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#666',
                backgroundColor: '#f8f9fa',
                borderRadius: '15px',
                border: '2px dashed #ccc'
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '20px',
                  opacity: '0.5'
                }}>
                  📅
                </div>
                <h4 style={{
                  margin: '0 0 10px 0',
                  fontSize: '18px',
                  color: '#333'
                }}>
                  لا توجد حصص في هذا اليوم
                </h4>
                <p style={{
                  margin: '0',
                  fontSize: '14px',
                  opacity: '0.7'
                }}>
                  استمتع بيوم راحة أو راجع جدول يوم آخر
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          padding: '25px 40px',
          textAlign: 'center',
          borderTop: '3px solid #017FD2',
          marginTop: '30px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#017FD2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                color: 'white'
              }}>
                3A
              </div>
              <span style={{
                color: '#017FD2',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                علمني
              </span>
            </div>
            
            <div style={{
              color: '#666',
              fontSize: '12px'
            }}>
              <p style={{ margin: '2px 0' }}>تم إنشاء هذا الجدول بواسطة</p>
              <p style={{ margin: '2px 0', fontWeight: 'bold' }}>نظام إدارة الحصص الدراسية</p>
            </div>
          </div>
          
          <div style={{
            borderTop: '1px solid #dee2e6',
            paddingTop: '15px',
            color: '#999',
            fontSize: '11px'
          }}>
            <p style={{ margin: '0' }}>
              © 2024 علمني - جميع الحقوق محفوظة | تم إنشاء هذا المستند في {new Date().toLocaleDateString('ar-EG')} الساعة {new Date().toLocaleTimeString('ar-EG')}
            </p>
          </div>
        </div>
      </div>

      {/* Reminders Panel */}
      {showReminders && (
        <div className={styles['jadwal-reminders-panel']}>
          <div className={styles['jadwal-reminders-header']}>
            <h3>التذكيرات ({getRemindersForToday().length})</h3>
            <div className={styles['jadwal-reminders-actions']}>
              <button 
                className={styles['jadwal-clear-all-btn']}
                onClick={clearAllReminders}
                title="حذف جميع التذكيرات"
              >
                حذف الكل
              </button>
              <button 
                className={styles['jadwal-close-reminders']}
                onClick={handleReminderToggle}
              >
                ×
              </button>
            </div>
          </div>
          <div className={styles['jadwal-reminders-list']}>
            {getRemindersForToday().length > 0 ? (
              getRemindersForToday()
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map(reminder => (
                <div key={reminder.id} className={styles['jadwal-reminder-item']}>
                  <div className={styles['jadwal-reminder-info']}>
                    <h4>{reminder.subject}</h4>
                    <p>{reminder.time} - {reminder.teacher}</p>
                    <small>{reminder.date.toLocaleDateString('ar-EG')}</small>
                  </div>
                  <button
                    className={styles['jadwal-remove-reminder-btn']}
                    onClick={() => removeReminder(reminder.id)}
                    title="حذف التذكير"
                  >
                    ×
                  </button>
                </div>
              ))
            ) : (
              <div className={styles['jadwal-no-reminders']}>
                <FaBell />
                <p>لا توجد تذكيرات</p>
                <small>اضغط على أيقونة الجرس بجانب الحصة لإضافة تذكير</small>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleTable; 